import { useState } from 'react'

import { InputAdornment, MenuItem, Select, TextField } from '@material-ui/core'
import { useField } from 'formik'
import { AsYouType, isValidPhoneNumber } from 'libphonenumber-js'
import ReactCountryFlag from 'react-country-flag'
import * as RPN from 'react-phone-number-input'
import en from 'react-phone-number-input/locale/en.json'

const countries = RPN.getCountries()

const CountrySelect = ({ value, onChange }: { value: RPN.Country; onChange: (value: RPN.Country) => void }) => {
  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value as RPN.Country)}
      disableUnderline
      renderValue={(selected) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <ReactCountryFlag
            countryCode={selected as string}
            svg
            style={{ width: '24px', height: '24px' }}
          />
        </div>
      )}
    >
      {countries.map((c) => (
        <MenuItem
          key={c}
          value={c}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ReactCountryFlag
              countryCode={c}
              svg
              style={{ width: '24px', height: '24px' }}
            />
            <span>{en[c] || c}</span>
            <span style={{ marginLeft: 'auto', opacity: 0.5 }}>{c}</span>
          </div>
        </MenuItem>
      ))}
    </Select>
  )
}

type Props = {
  name: string
  label?: string
  required?: boolean
}

const PhoneNumberInput = ({ name, label, required }: Props) => {
  const [displayValue, setDisplayValue] = useState('')
  const [, meta, helpers] = useField({
    name,
    validate: (value: string) => {
      if (!value) return undefined
      if (!isValidPhoneNumber('+' + value)) return 'Invalid phone number'
    },
  })
  const [country, setCountry] = useState<RPN.Country>('US')

  const showError = meta.touched && Boolean(meta.error)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '')
    const formatter = new AsYouType(country)
    setDisplayValue(formatter.input(digits).trimEnd())
    const callingCode = String(RPN.getCountryCallingCode(country))
    helpers.setValue(digits ? callingCode + digits : '')
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Backspace') return
    const digits = displayValue.replace(/\D/g, '')
    if (!digits) return
    e.preventDefault()
    const newDigits = digits.slice(0, -1)
    const formatter = new AsYouType(country)
    setDisplayValue(newDigits ? formatter.input(newDigits).trimEnd() : '')
    const callingCode = String(RPN.getCountryCallingCode(country))
    helpers.setValue(newDigits ? callingCode + newDigits : '')
  }

  return (
    <TextField
      name={name}
      value={displayValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={() => helpers.setTouched(true)}
      variant="outlined"
      fullWidth
      label={label || 'Phone Number'}
      required={required}
      error={showError}
      helperText={showError ? meta.error : ''}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <CountrySelect
              value={country}
              onChange={setCountry}
            />
            +{RPN.getCountryCallingCode(country)}
          </InputAdornment>
        ),
      }}
    />
  )
}

export default PhoneNumberInput
